import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  ColorPicker,
  ColorSwatch,
  Group,
  Input,
  Stack,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
  getGetDepartmentsOptionallyfilteredbylocationQueryKey,
  useUpdateDepartment,
} from "@/shared/api/generated/departments";
import { GetDepartmentData } from "@/shared/api/generated/schemas";
import { useFormWatch } from "@/shared/utils/forms/useFormWatch";

function useFormSchema() {
  const { t } = useLingui();

  return z.object({
    name: z.string().min(1, { message: t`Navn er obligatorisk` }),
    calendarColor: z.string().optional(),
  });
}

type UpdateDepartmentFormValues = z.infer<ReturnType<typeof useFormSchema>>;

export default function UpdateDepartmentForm({
  department,
  close,
}: {
  department: GetDepartmentData;
  close: () => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const formSchema = useFormSchema();

  const { mutateAsync: updateDepartment } = useUpdateDepartment({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getGetDepartmentsOptionallyfilteredbylocationQueryKey(),
        }),
    },
  });

  const form = useForm<UpdateDepartmentFormValues>({
    mode: "controlled",
    validate: zodResolver(formSchema),
    initialValues: {
      name: department.name,
      calendarColor: department.calendarColor ?? "",
    },
  });

  const selectedColor = useFormWatch(form, "calendarColor");

  const theme = useMantineTheme();

  // Calendar event colors - using medium saturation shades (5-6) for good visibility and contrast
  const calendarColors = [
    theme.colors.blue[5],
    theme.colors.indigo[5],
    theme.colors.cyan[5],
    theme.colors.teal[5],
    theme.colors.green[5],
    theme.colors.lime[5],
    theme.colors.yellow[5],
    theme.colors.orange[5],
    theme.colors.red[5],
    theme.colors.pink[5],
    theme.colors.grape[5],
    theme.colors.violet[5],
    theme.colors.primary[5],
  ];

  async function handleSubmit(values: UpdateDepartmentFormValues) {
    await updateDepartment(
      {
        id: department.id,
        data: {
          name: values.name,
          locationId: department.location.id,
          calendarColor: values.calendarColor ?? null,
        },
      },
      {
        onSuccess: close,
      },
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label={t`Navn`}
          placeholder={t`Velg navn`}
          withAsterisk
          {...form.getInputProps("name")}
        />

        <Group align="flex-start" justify="space-between">
          <Input.Wrapper
            label={t`Farge`}
            description={t`Fargen som velges blir synlig i kalendern`}
          >
            <ColorPicker
              {...form.getInputProps("calendarColor")}
              withPicker={false}
              swatches={calendarColors}
              onColorSwatchClick={(color) => {
                form.setFieldValue(
                  "calendarColor",
                  selectedColor === color ? "" : color,
                );
              }}
            />
          </Input.Wrapper>

          <Input.Wrapper label={t`Forhåndsvisning`}>
            <ColorSwatch
              mt={8}
              size={64}
              style={{ "--cs-radius": "var(--mantine-radius-sm)" }}
              color={selectedColor ?? ""}
            />
          </Input.Wrapper>
        </Group>

        <Group justify="end" gap={8} mt={8}>
          <Button variant="subtle" onClick={close}>
            <Trans>Avbryt</Trans>
          </Button>
          <Button type="submit">
            <Trans>Oppdater</Trans>
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
